export interface PlanData {
  plan: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
}

type RawPlan = PlanData & { authored: Date };

function parsePlanStatus(statusItem?: fhir5.QuestionnaireResponseItem): string {
  let status = "aberta";
  const coding = statusItem?.answer?.[0]?.valueCoding;
  if (!coding) return status;
  const code = coding.code;
  const display = coding.display;
  if (code === "concluded" || display === "Concluído") return "concluído";
  if (code === "partially" || display === "Parcialmente") return "parcial";
  if (code === "failed" || display === "Falhou") return "falhou";
  return status;
}

export function processEntriesToPlans(
  entries: unknown[],
  getPractitionerName: (ref: string) => string
): PlanData[] {
  const raw: RawPlan[] = [];

  for (const entry of entries) {
    const resource = (entry as { resource?: fhir5.QuestionnaireResponse })?.resource;
    if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;

    const patientId = resource.subject?.reference || "unknown";
    const authoredDate = resource.authored ? new Date(resource.authored) : new Date(0);
    const date = authoredDate.toLocaleDateString();
    const practitionerName = getPractitionerName(resource.author?.reference || "");

    const plansItem = resource.item?.find(
      (item) => item.linkId === "4e1679c2-3e1d-444d-d438-28bdbeb1e0ee"
    );
    if (!plansItem?.item) continue;

    for (const planGroup of plansItem.item) {
      if (!planGroup.item || planGroup.linkId !== "4e1679c2-3e1d-444d-d438-28bdbeb1e0ee") {
        continue;
      }

      const planItem = planGroup.item.find(
        (item) => item.linkId === "477c970e-a76b-4fe9-89d4-65cd19481b7b"
      );
      const planText = planItem?.answer?.[0]?.valueString;
      if (!planText) continue;

      const statusItem = planGroup.item.find(
        (item) => item.linkId === "ba000da7-3e8e-4b03-faf2-4089aeb2852d"
      );

      raw.push({
        plan: planText,
        status: parsePlanStatus(statusItem),
        practitioner: practitionerName,
        date,
        patientId,
        authored: authoredDate,
      });
    }
  }

  const patientLatestAuthored = new Map<string, number>();
  for (const v of raw) {
    const t = v.authored.getTime();
    const prev = patientLatestAuthored.get(v.patientId) || 0;
    if (t > prev) patientLatestAuthored.set(v.patientId, t);
  }

  const groups = new Map<string, RawPlan[]>();
  for (const it of raw) {
    const key = `${it.patientId}|${it.plan}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(it);
  }

  const parity: PlanData[] = [];
  for (const [, arr] of groups.entries()) {
    if (arr.length === 1) {
      const [one] = arr;
      parity.push({
        plan: one.plan,
        status: one.status,
        practitioner: one.practitioner,
        date: one.date,
        patientId: one.patientId,
      });
      continue;
    }

    const sorted = arr.sort((a, b) => b.authored.getTime() - a.authored.getTime());
    const isPair = arr.length % 2 === 0;
    const withStatus = sorted.filter((x) => x.status && x.status !== "aberta");
    const withoutStatus = sorted.filter((x) => !x.status || x.status === "aberta");
    const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
    const latestBelongsToCurrentQR =
      latestAuthoredTs !== undefined && sorted[0].authored.getTime() === latestAuthoredTs;

    const push = (item: RawPlan) =>
      parity.push({
        plan: item.plan,
        status: item.status,
        practitioner: item.practitioner,
        date: item.date,
        patientId: item.patientId,
      });

    if (latestBelongsToCurrentQR) {
      for (const item of sorted) push(item);
    } else if (isPair) {
      if (withStatus.length > 0) push(withStatus[0]);
      else push(sorted[0]);
    } else if (withStatus.length > 0) {
      push(withStatus[0]);
      if (withoutStatus.length > 0) push(withoutStatus[0]);
    } else {
      push(sorted[0]);
    }
  }

  return parity;
}
